import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Icon, Spinner, Input, Image } from "@chakra-ui/react";
import { ShieldCheck, MapPin, CheckCircle2, ArrowLeft, ChevronRight, HardHat } from "lucide-react";
import { FieldInput, isAnswered } from "@/components/FieldInput";
import { SignaturePad } from "@/components/SignaturePad";
import { computeFlagReasons } from "@/lib/submissionMeta";
import { colors } from "@/theme/colors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = { h: "48px", bg: colors.white, borderColor: colors.border, borderRadius: "10px", fontSize: "md" };

// PUBLIC no-login contractor portal. Reads/writes only through the token-scoped
// backend endpoints — never touches Supabase directly.
export default function PortalPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | invalid | ready
  const [info, setInfo] = useState(null);
  const [who, setWho] = useState(null); // { contractor_id?, name, company?, contact? }
  const [tpl, setTpl] = useState(null);
  const [doneId, setDoneId] = useState(null);

  useEffect(() => {
    fetch(`${API}/portal/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setInfo(d); setState("ready"); })
      .catch(() => setState("invalid"));
  }, [token]);

  if (state === "loading") {
    return <Flex minH="100vh" align="center" justify="center" bg={colors.page}><Spinner color={colors.teal} borderWidth="3px" /></Flex>;
  }
  if (state === "invalid") {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={colors.page} p="6" data-testid="portal-invalid">
        <Stack align="center" gap="3" textAlign="center" maxW="340px">
          <Icon as={ShieldCheck} boxSize="10" color={colors.textSecondary} />
          <Heading fontFamily="heading" fontSize="lg" color={colors.textPrimary}>This link isn't active</Heading>
          <Text fontSize="sm" color={colors.textSecondary}>Ask the site manager for the current QR code.</Text>
        </Stack>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg={colors.page} data-testid="portal-page">
      <Flex align="center" gap="2.5" px="5" py="3.5" bg={colors.white} borderBottom="1px solid" borderColor={colors.border}>
        <Icon as={ShieldCheck} boxSize="6" color={colors.teal} />
        <Box flex="1" minW="0">
          <Text fontWeight="700" fontSize="md" color={colors.textPrimary} fontFamily="heading">Hakina Safety</Text>
          <Text fontSize="xs" color={colors.textSecondary} data-testid="portal-company-name">
            for <b>{info.company_name}</b>
          </Text>
        </Box>
        {info.logo_url && <Image src={info.logo_url} alt={info.company_name} maxH="32px" maxW="84px" objectFit="contain" />}
      </Flex>

      <Box maxW="560px" mx="auto" px="4" py="6" pb="28">
        {(() => {
          // portal wizard steps flattened for readability (was a nested ternary)
          if (doneId) return <DoneScreen refId={doneId} onAnother={() => { setDoneId(null); setTpl(null); }} />;
          if (!who) return <IdentityStep contractors={info.contractors} onDone={setWho} />;
          if (!tpl) return <FormPick templates={info.templates} who={who} onPick={setTpl} onBack={() => setWho(null)} />;
          return <PortalFill token={token} info={info} tpl={tpl} who={who}
            onBack={() => setTpl(null)} onDone={setDoneId} />;
        })()}
      </Box>
    </Box>
  );
}

function IdentityStep({ contractors, onDone }) {
  const [notListed, setNotListed] = useState(false);
  const [free, setFree] = useState({ name: "", company: "", contact: "" });
  const [err, setErr] = useState(null);

  return (
    <Stack gap="3" data-testid="portal-identity-step">
      <Heading fontFamily="heading" fontSize="xl" color={colors.textPrimary}>Who are you?</Heading>
      <Text fontSize="sm" color={colors.textSecondary}>Pick your company, or enter your details if you're not on the list.</Text>

      {!notListed && contractors.map((c) => (
        <Flex key={c.id} as="button" type="button" onClick={() => onDone({ contractor_id: c.id, name: c.name })}
          data-testid={`portal-contractor-${c.id}`} w="full" textAlign="left" align="center" gap="3"
          bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" px="4" py="3.5"
          _hover={{ borderColor: colors.teal, bg: colors.tealTint }}>
          <Icon as={HardHat} boxSize="5" color={colors.teal} />
          <Box flex="1">
            <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{c.name}</Text>
            {c.contact_name && <Text fontSize="xs" color={colors.textSecondary}>{c.contact_name}</Text>}
          </Box>
          <Icon as={ChevronRight} boxSize="5" color={colors.textSecondary} />
        </Flex>
      ))}

      {!notListed ? (
        <Button variant="outline" onClick={() => setNotListed(true)} data-testid="portal-not-listed-btn">
          I'm not on the list
        </Button>
      ) : (
        <Stack gap="2.5" bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4">
          <Input {...inp} placeholder="Your name *" value={free.name} onChange={(e) => setFree({ ...free, name: e.target.value })} data-testid="portal-free-name" />
          <Input {...inp} placeholder="Your company" value={free.company} onChange={(e) => setFree({ ...free, company: e.target.value })} data-testid="portal-free-company" />
          <Input {...inp} placeholder="Email or phone" value={free.contact} onChange={(e) => setFree({ ...free, contact: e.target.value })} data-testid="portal-free-contact" />
          {err && <Text fontSize="sm" color={colors.criticalText} data-testid="portal-identity-error">{err}</Text>}
          <HStack gap="2">
            <Button flex="1" onClick={() => {
              if (!free.name.trim()) { setErr("Your name is required."); return; }
              onDone({ contractor_id: null, name: free.name.trim(), company: free.company.trim() || null, contact: free.contact.trim() || null });
            }} data-testid="portal-identity-continue">Continue</Button>
            {contractors.length > 0 && <Button variant="ghost" onClick={() => { setNotListed(false); setErr(null); }}>Back</Button>}
          </HStack>
        </Stack>
      )}
    </Stack>
  );
}

function FormPick({ templates, who, onPick, onBack }) {
  return (
    <Stack gap="3" data-testid="portal-form-pick">
      <Button size="xs" variant="ghost" alignSelf="flex-start" onClick={onBack} data-testid="portal-back-identity"><ArrowLeft size={14} /> {who.name}</Button>
      <Heading fontFamily="heading" fontSize="xl" color={colors.textPrimary}>Choose a form</Heading>
      {templates.length === 0 ? (
        <Text fontSize="sm" color={colors.textSecondary} data-testid="portal-no-forms">No forms are available right now — check with the site manager.</Text>
      ) : templates.map((t) => (
        <Flex key={t.id} as="button" type="button" onClick={() => onPick(t)} data-testid={`portal-form-${t.id}`}
          w="full" textAlign="left" align="center" gap="3" bg={colors.white} border="1px solid" borderColor={colors.border}
          borderRadius="12px" px="4" py="3.5" _hover={{ borderColor: colors.teal, bg: colors.tealTint }}>
          <Icon as={ShieldCheck} boxSize="5" color={colors.teal} />
          <Box flex="1">
            <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{t.title}</Text>
            <Text fontSize="xs" color={colors.textSecondary}>{t.type}</Text>
          </Box>
          <Icon as={ChevronRight} boxSize="5" color={colors.textSecondary} />
        </Flex>
      ))}
    </Stack>
  );
}

function PortalFill({ token, info, tpl, who, onBack, onDone }) {
  const sections = useMemo(() => tpl.schema?.sections || [], [tpl]);
  const needsLocation = !info.preset_location;
  const totalSteps = (needsLocation ? 1 : 0) + sections.length + 1; // sections + sign step
  const [step, setStep] = useState(0);
  const [locationId, setLocationId] = useState(info.preset_location?.id || null);
  const [answers, setAnswers] = useState({});
  const [sig, setSig] = useState(null);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const listsById = useMemo(() => Object.fromEntries((info.select_lists || []).map((l) => [l.id, l])), [info]);

  const secIndex = needsLocation ? step - 1 : step;
  const onSignStep = secIndex === sections.length;

  const next = () => {
    if (needsLocation && step === 0) {
      if (!locationId) { setErrors(["__location"]); return; }
    } else if (!onSignStep) {
      const missing = (sections[secIndex].fields || []).filter((fl) => fl.required && !isAnswered(fl, answers[fl.id]));
      if (missing.length) { setErrors(missing.map((fl) => fl.id)); return; }
    }
    setErrors([]);
    setStep(step + 1);
    window.scrollTo({ top: 0 });
  };

  const submit = async () => {
    if (!sig) { setErr("Please sign before submitting."); return; }
    setErr(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/portal/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_id: tpl.id, location_id: locationId,
          contractor_id: who.contractor_id || null,
          contractor_name: who.name, contractor_company: who.company || null, contractor_contact: who.contact || null,
          answers, signature: sig, completed_at: new Date().toISOString(),
          flagged: computeFlagReasons(tpl.schema, answers, listsById).length > 0,
          flag_reasons: computeFlagReasons(tpl.schema, answers, listsById),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Could not submit — please try again");
      onDone(json.submission_id);
    } catch (e) { setErr(e.message); } finally { setSubmitting(false); }
  };

  const stepTitle = needsLocation && step === 0 ? "Location"
    : onSignStep ? "Sign & submit"
    : sections[secIndex]?.title || `Section ${secIndex + 1}`;

  return (
    <Box data-testid="portal-fill">
      <Flex align="center" gap="2" mb="3">
        <Button size="xs" variant="ghost" onClick={() => (step === 0 ? onBack() : (setErrors([]), setStep(step - 1)))} data-testid="portal-fill-back"><ArrowLeft size={14} /></Button>
        <Box flex="1">
          <Text fontSize="xs" color={colors.textSecondary}>{tpl.title}</Text>
          <Text fontWeight="700" fontSize="md" color={colors.textPrimary}>{stepTitle}</Text>
        </Box>
        <Text fontSize="xs" color={colors.textSecondary}>{step + 1}/{totalSteps}</Text>
      </Flex>
      <Box h="6px" borderRadius="full" bg={colors.cream} mb="5">
        <Box h="full" borderRadius="full" bg={colors.teal} w={`${Math.max(Math.round((step / (totalSteps - 1)) * 100), 4)}%`} transition="width 0.3s ease" />
      </Box>

      {needsLocation && step === 0 ? (
        <Stack gap="2.5" data-testid="portal-location-step">
          <Text fontSize="sm" color={colors.textSecondary}>Which site are you working at?</Text>
          {info.locations.map((l) => (
            <Flex key={l.id} as="button" type="button" onClick={() => { setLocationId(l.id); setErrors([]); }}
              data-testid={`portal-loc-${l.id}`} w="full" textAlign="left" align="center" gap="3" px="4" py="3.5"
              borderRadius="12px" border="1.5px solid" borderColor={locationId === l.id ? colors.teal : colors.border}
              bg={locationId === l.id ? colors.tealTint : colors.white}>
              <Icon as={MapPin} boxSize="5" color={locationId === l.id ? colors.teal : colors.textSecondary} />
              <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{l.name}</Text>
            </Flex>
          ))}
          {errors.includes("__location") && <Text fontSize="sm" color={colors.criticalText} data-testid="portal-location-error">Please choose the site first.</Text>}
        </Stack>
      ) : !onSignStep ? (
        <Stack gap="5" data-testid={`portal-section-${secIndex}`}>
          {(sections[secIndex].fields || []).map((fl) => (
            <Box key={fl.id} data-testid={`portal-field-${fl.id}`}>
              {fl.type !== "info" && (
                <HStack gap="1" mb="2">
                  <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{fl.label}</Text>
                  {fl.required && <Text color={colors.poppy}>*</Text>}
                </HStack>
              )}
              <FieldInput field={fl} value={answers[fl.id]}
                onChange={(v) => { setAnswers((a) => ({ ...a, [fl.id]: v })); setErrors((e) => e.filter((x) => x !== fl.id)); }}
                listsById={listsById} workers={[]} />
              {errors.includes(fl.id) && <Text fontSize="sm" color={colors.criticalText} mt="1.5" data-testid={`portal-field-error-${fl.id}`}>This answer is required.</Text>}
            </Box>
          ))}
        </Stack>
      ) : (
        <Stack gap="4" data-testid="portal-sign-step">
          <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4">
            <Text fontSize="sm" color={colors.textPrimary}>
              Submitting as <b>{who.name}</b>{who.company && who.company !== who.name ? ` (${who.company})` : ""} at{" "}
              <b>{info.preset_location?.name || info.locations.find((l) => l.id === locationId)?.name}</b>.
            </Text>
            <Text fontSize="xs" color={colors.textSecondary} mt="1">Submissions are permanent and can't be edited afterwards.</Text>
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} mb="1.5">Your signature *</Text>
            <SignaturePad value={sig} onChange={setSig} testId="portal-signature" />
          </Box>
          {err && <Text fontSize="sm" color={colors.criticalText} data-testid="portal-submit-error">{err}</Text>}
        </Stack>
      )}

      <Flex position="fixed" bottom="0" left="0" right="0" zIndex={100} bg={colors.white}
        borderTop="1px solid" borderColor={colors.border} px="4" pt="3" pb={{ base: "6", md: "3" }} justify="center">
        <HStack gap="2" w="full" maxW="560px">
          {!onSignStep ? (
            <Button flex="1" onClick={next} data-testid="portal-next-btn">Next</Button>
          ) : (
            <Button flex="1" onClick={submit} loading={submitting} data-testid="portal-submit-btn">Submit</Button>
          )}
        </HStack>
      </Flex>
    </Box>
  );
}

function DoneScreen({ refId, onAnother }) {
  return (
    <Flex minH="50vh" align="center" justify="center" data-testid="portal-done">
      <Stack align="center" gap="4" textAlign="center" maxW="340px">
        <Icon as={CheckCircle2} boxSize="14" color={colors.sageDeep} />
        <Heading fontFamily="heading" fontSize="xl" color={colors.textPrimary}>Submitted — thank you!</Heading>
        <Text fontSize="sm" color={colors.textSecondary}>
          Your form was sent to the site's safety team. Reference: <b>{refId?.slice(0, 8)}</b>
        </Text>
        <Button onClick={onAnother} data-testid="portal-fill-another">Fill another form</Button>
      </Stack>
    </Flex>
  );
}
